import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { Toaster } from "./components/ui/toaster";
import { HelmetProvider } from "react-helmet-async";
import { LoadingScreen } from "./components/ui/spinner";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";

// MotoPotti SaaS Pages
import MotoPottiLandingPage from "./pages/MotoPottiLandingPage";
import MotoPottiLandingPageV2 from "./pages/MotoPottiLandingPageV2";
import MotoPottiAdminPage from "./pages/MotoPottiAdminPage";
import HardwareStorePage from "./pages/HardwareStorePage";

// Garage Public Pages (for each tenant)
import LandingPage from "./pages/LandingPage";
import ServicesPage from "./pages/ServicesPage";
import StorePage from "./pages/StorePage";
// Legacy fallbacks (rollback safety) — preserve original site at /:slug/legacy
import LandingPageLegacy from "./pages/LandingPageLegacy";
import ServicesPageLegacy from "./pages/ServicesPageLegacy";
import StorePageLegacy from "./pages/StorePageLegacy";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";

// Garage Admin Pages
import DashboardPage from "./pages/admin/DashboardPage";
import JobsListPage from "./pages/admin/JobsListPage";
import JobCreatePage from "./pages/admin/JobCreatePage";
import JobDetailPage from "./pages/admin/JobDetailPage";
import JobEditPage from "./pages/admin/JobEditPage";
import CustomersListPage from "./pages/admin/CustomersListPage";
import CustomerCreatePage from "./pages/admin/CustomerCreatePage";
import CustomerDetailPage from "./pages/admin/CustomerDetailPage";
import CustomerEditPage from "./pages/admin/CustomerEditPage";
import InventoryPage from "./pages/admin/InventoryPage";
import InvoicesListPage from "./pages/admin/InvoicesListPage";
import InvoiceDetailPage from "./pages/admin/InvoiceDetailPage";
import ReportsPage from "./pages/admin/ReportsPage";
import SettingsPage from "./pages/admin/SettingsPage";
import WebsiteHubPage from "./pages/admin/WebsiteHubPage";
import ServicesManagementPage from "./pages/admin/ServicesManagementPage";
import DuesPage from "./pages/admin/DuesPage";
import EmployeesListPage from "./pages/admin/EmployeesListPage";
import EmployeeCreatePage from "./pages/admin/EmployeeCreatePage";
import EmployeeDetailPage from "./pages/admin/EmployeeDetailPage";
import EmployeeEditPage from "./pages/admin/EmployeeEditPage";
import MarketingToolsPage from "./pages/admin/MarketingToolsPage";

// Protected Route Component for Garage Admin
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={user.tenant_slug ? `/${user.tenant_slug}/admin` : '/admin'} replace />;
  }

  return children;
}

// Super Admin Route
function SuperAdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user || user.role !== 'super_admin') {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public Route (redirect if logged in)
function PublicOnlyRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (user) {
    // Redirect based on user role
    if (user.role === 'super_admin') {
      return <Navigate to="/motopotti-admin" replace />;
    }
    if (user.tenant_slug) {
      return <Navigate to={`/${user.tenant_slug}/admin`} replace />;
    }
    return <Navigate to="/admin" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* MotoPotti SaaS Landing Page */}
      <Route path="/" element={<MotoPottiLandingPageV2 />} />
      <Route path="/landing-v1" element={<MotoPottiLandingPage />} />
      <Route path="/hardware" element={<HardwareStorePage />} />
      
      {/* MotoPotti Super Admin Panel */}
      <Route 
        path="/motopotti-admin" 
        element={
          <SuperAdminRoute>
            <MotoPottiAdminPage />
          </SuperAdminRoute>
        } 
      />

      {/* Global Auth Pages (for super admin or legacy) */}
      <Route 
        path="/login" 
        element={
          <PublicOnlyRoute>
            <LoginPage />
          </PublicOnlyRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicOnlyRoute>
            <RegisterPage />
          </PublicOnlyRoute>
        } 
      />

      {/* Legacy Admin Routes (redirect to proper location) */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/*" 
        element={
          <ProtectedRoute>
            <Routes>
              <Route path="jobs" element={<JobsListPage />} />
              <Route path="jobs/new" element={<JobCreatePage />} />
              <Route path="jobs/:id" element={<JobDetailPage />} />
              <Route path="jobs/:id/edit" element={<JobEditPage />} />
              <Route path="customers" element={<CustomersListPage />} />
              <Route path="customers/new" element={<CustomerCreatePage />} />
              <Route path="customers/:id" element={<CustomerDetailPage />} />
              <Route path="customers/:id/edit" element={<CustomerEditPage />} />
              <Route path="employees" element={<EmployeesListPage />} />
              <Route path="employees/new" element={<EmployeeCreatePage />} />
              <Route path="employees/:id" element={<EmployeeDetailPage />} />
              <Route path="employees/:id/edit" element={<EmployeeEditPage />} />
              <Route path="inventory" element={<InventoryPage />} />
              <Route path="services" element={<ServicesManagementPage />} />
              <Route path="invoices" element={<InvoicesListPage />} />
              <Route path="invoices/new" element={<InvoicesListPage />} />
              <Route path="invoices/dues" element={<DuesPage />} />
              <Route path="invoices/:id" element={<InvoiceDetailPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="marketing" element={<MarketingToolsPage />} />
              <Route path="website" element={<WebsiteHubPage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Routes>
          </ProtectedRoute>
        } 
      />

      {/* Garage-specific Public Pages */}
      <Route path="/:garageSlug" element={<LandingPage />} />
      <Route path="/:garageSlug/services" element={<ServicesPage />} />
      <Route path="/:garageSlug/store" element={<StorePage />} />

      {/* Legacy (rollback) versions — original tenant site preserved untouched */}
      <Route path="/:garageSlug/legacy" element={<LandingPageLegacy />} />
      <Route path="/:garageSlug/legacy/services" element={<ServicesPageLegacy />} />
      <Route path="/:garageSlug/legacy/store" element={<StorePageLegacy />} />
      
      {/* Garage-specific Admin Routes */}
      <Route 
        path="/:garageSlug/admin" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/jobs" 
        element={
          <ProtectedRoute>
            <JobsListPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/jobs/new" 
        element={
          <ProtectedRoute>
            <JobCreatePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/jobs/:id" 
        element={
          <ProtectedRoute>
            <JobDetailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/jobs/:id/edit" 
        element={
          <ProtectedRoute>
            <JobEditPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/customers" 
        element={
          <ProtectedRoute>
            <CustomersListPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/customers/new" 
        element={
          <ProtectedRoute>
            <CustomerCreatePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/customers/:id" 
        element={
          <ProtectedRoute>
            <CustomerDetailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/customers/:id/edit" 
        element={
          <ProtectedRoute>
            <CustomerEditPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/employees" 
        element={
          <ProtectedRoute>
            <EmployeesListPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/employees/new" 
        element={
          <ProtectedRoute>
            <EmployeeCreatePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/employees/:id" 
        element={
          <ProtectedRoute>
            <EmployeeDetailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/employees/:id/edit" 
        element={
          <ProtectedRoute>
            <EmployeeEditPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/inventory" 
        element={
          <ProtectedRoute>
            <InventoryPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/services" 
        element={
          <ProtectedRoute>
            <ServicesManagementPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/invoices" 
        element={
          <ProtectedRoute>
            <InvoicesListPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/invoices/new" 
        element={
          <ProtectedRoute>
            <InvoicesListPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/invoices/dues" 
        element={
          <ProtectedRoute>
            <DuesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/invoices/:id" 
        element={
          <ProtectedRoute>
            <InvoiceDetailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/reports" 
        element={
          <ProtectedRoute allowedRoles={['owner', 'manager', 'super_admin']}>
            <ReportsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/marketing" 
        element={
          <ProtectedRoute allowedRoles={['owner', 'manager', 'super_admin']}>
            <MarketingToolsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/settings" 
        element={
          <ProtectedRoute allowedRoles={['owner', 'super_admin']}>
            <SettingsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/:garageSlug/admin/website" 
        element={
          <ProtectedRoute allowedRoles={['owner', 'manager', 'super_admin']}>
            <WebsiteHubPage />
          </ProtectedRoute>
        } 
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <div className="App">
              <AppRoutes />
              <Toaster />
              <SpeedInsights />
              <Analytics />
            </div>
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
